/**
 * App Bridge Handler
 * 
 * Provides browser-equivalent implementations for SDK app-bridge requests.
 * Games using beemi.app.* APIs will have their requests handled here
 * with web platform alternatives.
 * 
 * Supported actions:
 * - auth.promptSignIn - Redirect to login page
 * - navigation.openUrl - Open in new tab
 * - device.copyToClipboard - Use Clipboard API
 * - device.getInfo - Return browser/platform info
 * - device.vibrate - Use Vibration API if available
 * - util.share - Use Web Share API with fallback
 * - app.getInfo - Return webplay app info
 */

import { gameBridgeService, AppBridgeRequest } from './GameBridgeService';

// ============================================================================
// Types
// ============================================================================

export interface AppBridgeHandlerConfig {
  /** Callback when auth.promptSignIn is requested */
  onAuthPrompt?: () => void;
  /** Webplay version string */
  appVersion?: string;
  /** Current user for share functionality */
  getUser?: () => { id: string; username: string } | null;
}

interface DeviceInfo {
  platform: string;
  version: string;
  model: string;
  appVersion: string;
  isWebplay: boolean;
  userAgent: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
}

// ============================================================================
// App Bridge Handler Class
// ============================================================================

class AppBridgeHandler {
  private config: AppBridgeHandlerConfig = {};
  private unsubscribe: (() => void) | null = null;

  /**
   * Initialize the handler with configuration
   */
  initialize(config: AppBridgeHandlerConfig = {}): void {
    this.config = config;
    
    // Subscribe to game messages
    this.unsubscribe = gameBridgeService.onMessage((message) => {
      if (message.type === 'beemi-app-bridge') {
        this.handleRequest(message as AppBridgeRequest);
      }
    });

    console.log('[AppBridgeHandler] Initialized');
  }

  /**
   * Cleanup the handler
   */
  cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    console.log('[AppBridgeHandler] Cleaned up');
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AppBridgeHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Handle an app-bridge request from the game
   */
  private async handleRequest(request: AppBridgeRequest): Promise<void> {
    const { action, payload, requestId, fireAndForget } = request;
    
    console.log('[AppBridgeHandler] Received request:', action, payload);

    try {
      const result = await this.executeAction(action, payload);
      
      // Send response if not fire-and-forget
      if (!fireAndForget) {
        gameBridgeService.sendAppBridgeResponse(requestId, true, result);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[AppBridgeHandler] Request failed:', action, errorMessage);
      
      if (!fireAndForget) {
        gameBridgeService.sendAppBridgeResponse(requestId, false, undefined, errorMessage);
      }
    }
  }

  /**
   * Execute an app-bridge action
   */
  private async executeAction(action: string, payload: any): Promise<any> {
    switch (action) {
      case 'auth.promptSignIn':
        return this.handleAuthPromptSignIn();

      case 'navigation.navigateTo':
        return this.handleNavigateTo(payload);

      case 'navigation.openUrl':
        return this.handleOpenUrl(payload);

      case 'device.getInfo':
        return this.handleDeviceGetInfo();

      case 'device.copyToClipboard':
        return this.handleCopyToClipboard(payload);

      case 'device.vibrate':
        return this.handleVibrate(payload);

      case 'util.share':
        return this.handleShare(payload);

      case 'app.getInfo':
        return this.handleAppGetInfo();

      default:
        console.warn('[AppBridgeHandler] Unknown action:', action);
        return {
          success: false,
          message: `Unknown action: ${action}`,
          action
        };
    }
  }

  // ===========================================================================
  // Action Handlers
  // ===========================================================================

  /**
   * Handle auth.promptSignIn - Trigger webplay login
   */
  private async handleAuthPromptSignIn(): Promise<any> {
    if (this.config.onAuthPrompt) {
      this.config.onAuthPrompt();
    } else {
      // Fallback: redirect to login page
      window.location.href = '/login';
    }

    return {
      success: true,
      message: 'Sign in prompt triggered'
    };
  }

  /**
   * Handle navigation.navigateTo - Navigate within app
   */
  private async handleNavigateTo(payload: { screen: string; params?: any }): Promise<any> {
    const { screen, params } = payload;
    
    // Map SDK screen names to webplay routes
    const routeMap: Record<string, string> = {
      'Home': '/',
      'Profile': '/profile',
      'Search': '/search',
      'Settings': '/profile',
    };

    const route = routeMap[screen] || '/';
    
    // We can't navigate the parent window easily from here,
    // so we return the info for the parent to handle
    return {
      success: true,
      navigatedTo: screen,
      route,
      params
    };
  }

  /**
   * Handle navigation.openUrl - Open URL in new tab
   */
  private async handleOpenUrl(payload: { url: string }): Promise<any> {
    const { url } = payload;
    
    if (!url) {
      throw new Error('URL is required');
    }

    // Open in new tab with security attributes
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    return {
      success: !!newWindow,
      openedUrl: url
    };
  }

  /**
   * Handle device.getInfo - Return browser/platform info
   */
  private async handleDeviceGetInfo(): Promise<DeviceInfo> {
    const userAgent = navigator.userAgent;
    
    // Detect platform
    let platform = 'unknown';
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      platform = 'iOS';
    } else if (/Android/.test(userAgent)) {
      platform = 'Android';
    } else if (/Windows/.test(userAgent)) {
      platform = 'Windows';
    } else if (/Mac/.test(userAgent)) {
      platform = 'macOS';
    } else if (/Linux/.test(userAgent)) {
      platform = 'Linux';
    }

    // Get browser version
    let version = 'unknown';
    const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
    const firefoxMatch = userAgent.match(/Firefox\/(\d+)/);
    const safariMatch = userAgent.match(/Version\/(\d+)/);
    
    if (chromeMatch) {
      version = `Chrome ${chromeMatch[1]}`;
    } else if (firefoxMatch) {
      version = `Firefox ${firefoxMatch[1]}`;
    } else if (safariMatch) {
      version = `Safari ${safariMatch[1]}`;
    }

    return {
      platform,
      version,
      model: navigator.platform || 'Browser',
      appVersion: this.config.appVersion || '1.0.0',
      isWebplay: true,
      userAgent,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height
    };
  }

  /**
   * Handle device.copyToClipboard - Copy text to clipboard
   */
  private async handleCopyToClipboard(payload: { text: string }): Promise<any> {
    const { text } = payload;
    
    if (!text) {
      throw new Error('Text is required');
    }

    try {
      await navigator.clipboard.writeText(text);
      return {
        success: true,
        copiedText: text
      };
    } catch (error) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        return {
          success: true,
          copiedText: text,
          method: 'fallback'
        };
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }

  /**
   * Handle device.vibrate - Vibrate device if supported
   */
  private async handleVibrate(payload: { pattern?: number[] }): Promise<any> {
    const { pattern = [100] } = payload;
    
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
      return {
        success: true,
        pattern
      };
    }

    return {
      success: false,
      message: 'Vibration not supported'
    };
  }

  /**
   * Handle util.share - Use Web Share API or fallback
   */
  private async handleShare(payload: { title?: string; text?: string; url?: string }): Promise<any> {
    const { title, text, url } = payload;
    
    // Try Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return {
          success: true,
          method: 'native',
          shared: { title, text, url }
        };
      } catch (error) {
        // User cancelled or share failed
        if ((error as Error).name === 'AbortError') {
          return {
            success: false,
            message: 'Share cancelled by user'
          };
        }
      }
    }

    // Fallback: copy to clipboard
    const shareText = [title, text, url].filter(Boolean).join('\n');
    
    try {
      await navigator.clipboard.writeText(shareText);
      return {
        success: true,
        method: 'clipboard',
        message: 'Content copied to clipboard',
        shared: { title, text, url }
      };
    } catch (error) {
      throw new Error('Share failed and clipboard copy failed');
    }
  }

  /**
   * Handle app.getInfo - Return webplay app info
   */
  private async handleAppGetInfo(): Promise<any> {
    return {
      name: 'Beemi Webplay',
      version: this.config.appVersion || '1.0.0',
      build: '1',
      platform: 'web',
      isWebplay: true
    };
  }
}

// Export singleton instance
export const appBridgeHandler = new AppBridgeHandler();

// Also export the class for testing
export { AppBridgeHandler };

